import React, { useState, useEffect } from 'react';
import BookCard from './BookCard';
import LoadingSpinner from './LoadingSpinner';
import './Recommendations.css';
import { API_URL } from '../config/constants';

const Recommendations = ({ user }) => {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState('dashboard');

  useEffect(() => {
    fetchRecommendations();
  }, [selectedTab, user]);

  const fetchRecommendations = async () => {
    setLoading(true);
    setError(null);

    try {
      const headers = {};
      if (user?.token) {
        headers.Authorization = `Bearer ${user.token}`;
      }

      let endpoint = '';
      switch (selectedTab) {
        case 'personalized':
          if (!user?.token) {
            setError('Trebuie să fii autentificat pentru recomandări personalizate');
            setLoading(false);
            return;
          }
          endpoint = '/recommendations/personalized';
          break;
        case 'popular':
          endpoint = '/recommendations/popular';
          break;
        case 'collaborative':
          if (!user?.token) {
            setError('Trebuie să fii autentificat pentru recomandări collaborative');
            setLoading(false);
            return;
          }
          endpoint = '/recommendations/collaborative';
          break;
        default:
          endpoint = '/recommendations/dashboard';
      }

      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...headers
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (selectedTab === 'dashboard') {
        setRecommendations(data.recommendations || []);
      } else {
        // Pentru celelalte tab-uri, încadrează răspunsul într-o secțiune
        setRecommendations([{
          id: selectedTab,
          title: getTabTitle(selectedTab),
          description: getTabDescription(selectedTab),
          books: data.recommendations || []
        }]);
      }
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      setError('Nu s-au putut încărca recomandările. Te rugăm să încerci din nou.');
    } finally {
      setLoading(false);
    }
  };

  const getTabTitle = (tab) => {
    const titles = {
      personalized: 'Recomandări pentru tine',
      popular: 'Cele mai populare cărți',
      collaborative: 'Cititori ca tine au apreciat'
    };
    return titles[tab] || 'Recomandări';
  };

  const getTabDescription = (tab) => {
    const descriptions = {
      personalized: 'Pe baza preferințelor și istoricului tău de lectură',
      popular: 'Cărți apreciate de întreaga comunitate',
      collaborative: 'Cărți recomandate de utilizatori cu gusturi similare'
    };
    return descriptions[tab] || '';
  };

  const recordInteraction = async (bookId, interactionType, metadata = {}) => {
    if (!user?.token) return;

    try {
      await fetch(`${API_URL}/recommendations/interaction`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.token}`
        },
        body: JSON.stringify({
          bookId,
          interactionType,
          ...metadata
        })
      });
    } catch (error) {
      console.error('Error recording interaction:', error);
    }
  };

  const handleBookClick = (book) => {
    recordInteraction(book.id, 'view', {
      metadata: { source: 'recommendations', section: selectedTab }
    });
  };

  const handleBookFavorite = (book) => {
    recordInteraction(book.id, 'favorite');
  };

  if (loading) {
    return (
      <div className="recommendations-container">
        <LoadingSpinner size="large" />
        <p>Se încarcă recomandările...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="recommendations-container">
        <div className="error-message">
          <h3>❌ Eroare</h3>
          <p>{error}</p>
          <button onClick={fetchRecommendations} className="retry-button">
            Încearcă din nou
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="recommendations-container">
      <div className="recommendations-header">
        <h2>🎯 Recomandări pentru tine</h2>
        <p>Descoperă cărți pe baza preferințelor tale și a celor citite anterior</p>
      </div>

      <div className="recommendations-tabs">
        <button
          className={`tab-button ${selectedTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => setSelectedTab('dashboard')}
        >
          📊 Dashboard
        </button>
        {user?.token && (
          <button
            className={`tab-button ${selectedTab === 'personalized' ? 'active' : ''}`}
            onClick={() => setSelectedTab('personalized')}
          >
            👤 Personalizate
          </button>
        )}
        <button
          className={`tab-button ${selectedTab === 'popular' ? 'active' : ''}`}
          onClick={() => setSelectedTab('popular')}
        >
          🔥 Populare
        </button>
        {user?.token && (
          <button
            className={`tab-button ${selectedTab === 'collaborative' ? 'active' : ''}`}
            onClick={() => setSelectedTab('collaborative')}
          >
            🤝 Collaborative
          </button>
        )}
      </div>

      <div className="recommendations-content">
        {recommendations.length === 0 ? (
          <div className="no-recommendations">
            <h3>📚 Nu există recomandări momentan</h3>
            <p>
              {user?.token 
                ? 'Continuă să interacționezi cu cărțile pentru a primi recomandări personalizate!'
                : 'Autentifică-te pentru a primi recomandări personalizate!'
              }
            </p>
          </div>
        ) : (
          recommendations.map((section) => (
            <div key={section.id} className="recommendation-section">
              <div className="section-header">
                <h3>{section.title}</h3>
                {section.description && (
                  <p className="section-description">{section.description}</p>
                )}
              </div>
              
              {section.books && section.books.length > 0 ? (
                <div className="books-grid">
                  {section.books.map((book) => (
                    <div 
                      key={`${section.id}-${book.id}`}
                      onClick={() => handleBookClick(book)}
                    >
                      <BookCard
                        id={book.id}
                        title={book.title}
                        author={book.author}
                        description={book.description}
                        coverImage={book.coverImage}
                        category={book.category}
                        fileName={book.fileName}
                        fileType={book.fileType}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-books-in-section">
                  <p>Nu există cărți disponibile în această secțiune momentan.</p>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {user?.token && (
        <div className="recommendations-footer">
          <div className="preferences-info">
            <h4>💡 Sfat</h4>
            <p>
              Cu cât interacționezi mai mult cu cărțile (citești, salvezi în favorite, 
              scrii recenzii), cu atât recomandările vor fi mai precise!
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Recommendations; 
